import { changedSinceCopied } from "@shared/lib/followed-course"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditPaste } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type {
  ParentCardStack,
  ProseMirrorContent,
  UnifiedCardStack,
} from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { partial } from "lodash-es"
import type { PreparedAction } from "../../action-doc-builder"
import { followContext } from "../course/COURSE_FOLLOW_COURSE"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"
import LESSON_FIRST_CARDS_EDIT from "./LESSON_FIRST_CARDS_EDIT"
import LESSON_FIRST_EDIT from "./LESSON_FIRST_EDIT"
import { unifiedContentPatch } from "./unified-content-patch"

interface Payload {
  cardStack: UnifiedCardStack
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  followedLesson: UnifiedCardStack
  /** The followed class's day the lesson was copied from. */
  followedDateString: string
  parentCardStack: ParentCardStack
  proseMirror: ProseMirrorContent
  originalProseMirror: ProseMirrorContent
  originalTitle: string | null
  originalParentCardStack: ParentCardStack | null
}

export function prepareLessonCopyFollowedLesson(payload: Payload): PreparedAction {
  return {
    name: "LESSON_COPY_FOLLOWED_LESSON",
    payload: payload,
    patches: patches(payload),
    undoPatches: undoPatches(payload),
    narrative: partial(narrative, payload),
  }
}

function patches(payload: Payload): Array<PatchModel> {
  const patch = unifiedContentPatch(payload.cardStack.id, payload.proseMirror)
    .set("attributes.title", payload.followedLesson.attributes.title)
    .set("attributes.parentCardStack", payload.parentCardStack).patch

  const patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStack.id).set(
    "attributes.title",
    payload.followedLesson.attributes.title,
  ).patch

  return [...lessonSetupPatches(payload), patchForCardStackSummary, patch]
}

function undoPatches(payload: Payload): Array<PatchModel> {
  const patch = unifiedContentPatch(payload.cardStack.id, payload.originalProseMirror)
    .set("attributes.title", payload.originalTitle)
    .set("attributes.parentCardStack", payload.originalParentCardStack).patch

  const patchForCardStackSummary = Patch.create("card-stack-summary", payload.cardStack.id).set(
    "attributes.title",
    payload.originalTitle,
  ).patch

  return [patchForCardStackSummary, patch, ...lessonSetupUndoPatches(payload)]
}

function lessonSetupPatches(payload: Payload): Array<PatchModel> {
  if (payload.cardStack.meta?.isNewLesson === true) {
    return LESSON_FIRST_EDIT.patches({ ...payload, setCards: true })
  }
  if (
    payload.cardStack.meta?.cardsUpdatingFromTemplate === true ||
    payload.cardStack.attributes.cards === null
  ) {
    return LESSON_FIRST_CARDS_EDIT.patches(payload)
  }
  return []
}

function lessonSetupUndoPatches(payload: Payload): Array<PatchModel> {
  if (payload.cardStack.meta?.isNewLesson === true) {
    return LESSON_FIRST_EDIT.undoPatches(payload)
  }
  if (
    payload.cardStack.meta?.cardsUpdatingFromTemplate === true ||
    payload.cardStack.attributes.cards === null
  ) {
    return LESSON_FIRST_CARDS_EDIT.undoPatches(payload)
  }
  return []
}

function narrative(payload: Payload, findFn: FindFn, userId: string): Narrative | null {
  const models = defaultLessonModels(
    { ...payload, cardStackId: payload.cardStack.id },
    findFn,
    userId,
  )
  const context = defaultLessonContext(payload, models)

  context.donorCardStackTitle = payload.followedLesson.attributes.title
  context.donorCardStackId = payload.followedLesson.id
  context.editType = "paste"

  const newEvent: LessonEditPaste = {
    ...createBlankEvent(),
    root_id: models.planbook?.id ?? null,
    root_type: "planbook",
    date_plan_scheduled_for: models.dateStringForCardStack,
    primary_object_id: payload.cardStack.id,
    primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    primary_verb: "edit",
    primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
    primary_object_type: "lesson",
    primary_object_specifier: null,
    adverbial_verb: "pasting",
    adverbial_object_type: "lesson",
    adverbial_object_specifier: "single_lesson",
    adverbial_object_id: payload.followedLesson.id,
    adverbial_possessive: getPossessiveForPlanbook(
      findFn,
      userId,
      payload.followedLesson.relationships.planbook.data.id,
    ),
    adverbial_object_id_and_ancestor_ids: [
      payload.followedLesson.relationships.planbook.data.id,
      payload.followedLesson.relationships.course!.data!.id,
      payload.followedLesson.id,
    ],
  }

  const previousCopy =
    payload.originalParentCardStack?.id === payload.followedLesson.id
      ? payload.originalParentCardStack
      : null

  return {
    title: "Copied Lesson from Followed Class",
    titleForAnalytics: "Lesson - Paste",
    description: `Copied cards from ${
      payload.followedLesson.attributes.title || "a lesson"
    } into ${payload.originalTitle || "your lesson"}`,
    context: {
      ...context,
      ...followContext({
        followingCourseId: payload.course.id,
        followedCourseId: payload.followedLesson.relationships.course!.data!.id,
      }),
      followedDateString: payload.followedDateString,
      isRecopy: previousCopy !== null,
      changedSinceLastCopy:
        previousCopy !== null && changedSinceCopied(previousCopy, payload.followedLesson),
    },
    activity: defaultLessonActivity(payload, models),
    newAnalyticsEvents: [newEvent],
  }
}
